/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    Grid,
    Box,
    Typography,
    TextField,
    Button,
    GridJustification,
    useTheme,
    useMediaQuery,
    PropTypes as MuIPropTypes,
    CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field, FieldArray } from "formik";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import { colorBorder } from "../../../Constants/theme";
import { Location } from "../../Location";
import { ReactComponent as DocSVG } from "../../../Images/vetDoctor.svg";
import { ReactComponent as PencilSVG } from "../../../Images/pencil.svg";
import Api from "../../../helpers/apiHelpers";
import UploadPhoto from "../../Modals/UploadPhoto";
import MenuItem from "@material-ui/core/MenuItem";
import {specialities} from "../../../Constants/specialities";
import {getProfileURLUploadImage, getUserProfile, patchUserProfile} from "../../../redux/userProfile/userProfile.actions";
import {RootState, useAppDispatch} from "../../../redux/store";
import {useSelector} from "react-redux";
import AddLiscenses from "../../AddLiscenses";
import ModalImage from "react-modal-image";
import useWindowSize from "../../../utils/windowResizeHook";

interface Props {
    profile: object;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

declare global {
    interface String {
      toProperCase(): string;
    }
  }

const useStyle = makeStyles(() => ({
    wrapper: {
        padding: "1.5em"
    },
    vetCard: {
        boxShadow: "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        padding: "0 0 0 0",
        marginTop: "10px",
        borderRadius: "4px",
    },
    inputGrpup: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between"
    },
    clickable: {
        cursor: "pointer",
    },
    marginLeft: {
        marginLeft: "1em"
    },
    marginBottom: {
        marginBottom: "1em"
    },
    timeWrapper: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    titleSpace: {
        marginTop: "15px",
    },
    stateFieldStyles: {
        height: 40,
        width: "100%"
    },
    stateInputStyles: {
        width: "100%",
    },
    disallowSpacingVerticalPadding: {
        "& > div": {
            paddingTop: "40px !important",
            paddingBottom: "0px !important",
        }
    },
    workDaysCheckboxLabel: {
        minWidth: 140,
    },
    feeInputs: {
        maxWidth: 100,
        marginRight: "2em",
        marginTop: "1em"
    },
    img: {
        height: 100,
        objectFit: "cover"
    }
}));

const VetProfile = ({profile, setOpen}) => {
    const classes = useStyle();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const textAlignmentAdaptive: MuIPropTypes.Alignment = isMobile ? "center" : "left";
    const windowSize = useWindowSize();
    const [licenses, setLicenses] = useState([]);

    const imageRef = useRef(null);

    useEffect(() => {
        if (profile){
            if (Object.prototype.hasOwnProperty.call(profile, "licenseUrl")){
                setLicenses(profile?.licenseUrl);
            }
        }
    }, []);

    String.prototype.toProperCase = function () {
        return this.replace(/\w\S*/g, function(txt){return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();});
    };

    return (
        <>
            <Card variant="outlined" className={classes.wrapper}>
                <Typography variant="h4" gutterBottom>Veterinarian Profile</Typography>
                <Grid container className={windowSize.width <= 405 ? null : classes.vetCard}>
                    <Grid item container xs={windowSize.width <= 405 ? 12 : 5} sm={3}>
                        {profile?.hasOwnProperty("image") ?
                            <img
                                ref={imageRef}
                                src={profile.image}
                                width="130"
                                height="130"
                                style={windowSize.width <= 405 ? {objectFit: "cover", borderRadius: "4px"}:{objectFit: "cover", borderRadius: "4px 0 0 4px"}}
                            ></img>
                            : <DocSVG />
                        }
                    </Grid>
                    <Grid item container alignItems="center" xs={windowSize.width <= 405 ? 12 : 7} sm={9}>
                        <Grid item container xs={12} style={windowSize.width <= 405 ? null:{marginLeft: 10}}>
                            <Typography
                                variant={isMobile ? "h5":"h3"}
                            >
                                Dr. {profile?.lastName}
                            </Typography>
                        </Grid>
                        <Grid container>
                            <Grid item container alignItems="center" xs={12} style={windowSize.width <= 405 ? null : {marginLeft: 10}}>
                                <Typography
                                    variant={isMobile ? "body1":"h4"}
                                >
                                    {profile?.education}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Typography variant="h4" className={classes.titleSpace} gutterBottom>Species Treated:</Typography>
                <Grid container spacing={1}>
                    <Grid item container xs={12}>
                        <Typography>
                            {profile?.speciality.map(item => {return item.toProperCase().split("_" ).join(" ");}).join(", ")}
                        </Typography>
                    </Grid>
                </Grid>
                <Typography variant="h4" className={classes.titleSpace} gutterBottom>Bio:</Typography>
                <Grid container spacing={1} >
                    <Grid item container xs={12}>
                        <Typography>
                            {profile?.summary}
                        </Typography>
                    </Grid>
                </Grid>
                {licenses.length > 0 &&
					<>
					    <Typography variant="h4" className={classes.titleSpace} gutterBottom>Active License(s):</Typography>
					    <Box display="flex" flexWrap="wrap" justifyContent="space-between">
					        {licenses.map((item) => {
					            return (
					                <ModalImage
					                    key={item}
					                    small={item}
					                    large={item}
					                    className={classes.img}
					                />
					            );
					        })}
					    </Box>
					</>
                }
                <Box display="flex" justifyContent="flex-end" style={{marginTop:10}}>
                    <Button variant="contained" color="secondary" onClick={() => setOpen(false)}>Close</Button>
                </Box>
            </Card>
        </>
    );
};

export default VetProfile;
